import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BugReportService } from '../../firebase/unifiedDataService';
import ViewLayout2 from '../../components/ui/ViewLayout2';
import { AlertBanner } from '../../components/ui';
import { Logger } from '../../utils/logger';

const log = Logger.create('BugReportManagement');

/**
 * BugReportManagement - Admin view to manage bug reports
 * 
 * Uses unifiedDataService.BugReportService for consistent data access
 */
export default function BugReportManagement() {
  const navigate = useNavigate();
  
  const [bugReports, setBugReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);

  // Load bug reports via BugReportService
  useEffect(() => {
    loadBugReports();
  }, []);

  const loadBugReports = async () => {
    try {
      setLoading(true);
      const reports = await BugReportService.getAll();
      setBugReports(reports);
    } catch (err) {
      log.error('Error loading bug reports:', err);
      setError('Fehler beim Laden der Bug-Reports');
    } finally {
      setLoading(false);
    }
  };

  // Update bug report status
  const updateStatus = async (reportId, newStatus) => {
    try {
      await BugReportService.update(reportId, { status: newStatus });
      
      setBugReports(prev => prev.map(report => 
        report.id === reportId ? { ...report, status: newStatus } : report
      ));
      
      if (selectedReport?.id === reportId) {
        setSelectedReport(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      log.error('Error updating status:', err);
      setError('Fehler beim Aktualisieren des Status');
    }
  };

  // Update bug report priority
  const updatePriority = async (reportId, newPriority) => {
    try {
      await BugReportService.update(reportId, { priority: newPriority });
      
      setBugReports(prev => prev.map(report => 
        report.id === reportId ? { ...report, priority: newPriority } : report
      ));
      
      if (selectedReport?.id === reportId) {
        setSelectedReport(prev => ({ ...prev, priority: newPriority }));
      }
    } catch (err) {
      log.error('Error updating priority:', err);
      setError('Fehler beim Aktualisieren der Priorität');
    }
  };

  // Delete bug report
  const deleteBugReport = async (reportId) => {
    if (!confirm('Bug-Report wirklich löschen?')) return;
    
    try {
      await BugReportService.delete(reportId);
      setBugReports(prev => prev.filter(report => report.id !== reportId));
      
      if (selectedReport?.id === reportId) {
        setSelectedReport(null);
      }
    } catch (err) {
      log.error('Error deleting bug report:', err);
      setError('Fehler beim Löschen des Bug-Reports');
    }
  };

  // Filter bug reports
  const filteredReports = bugReports.filter(report => {
    const matchesStatus = filterStatus === 'all' || report.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || report.category === filterCategory;
    const matchesSearch = !searchTerm || 
      report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.userEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesCategory && matchesSearch;
  });

  // Stats
  const stats = {
    total: bugReports.length,
    new: bugReports.filter(r => r.status === 'new').length,
    inProgress: bugReports.filter(r => r.status === 'in-progress').length,
    resolved: bugReports.filter(r => r.status === 'resolved').length,
    closed: bugReports.filter(r => r.status === 'closed').length
  };

  // Category colors
  const getCategoryColor = (category) => {
    const colors = {
      ui: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      functionality: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      performance: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      data: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      other: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };
    return colors[category] || colors.other;
  };

  // Status colors
  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      'in-progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };
    return colors[status] || colors.new;
  };

  // Priority colors
  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-500',
      medium: 'text-yellow-600',
      high: 'text-red-600'
    };
    return colors[priority] || colors.medium;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <ViewLayout2 title="Bug-Reports" onBack="/admin">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      </ViewLayout2>
    );
  }

  return (
    <ViewLayout2
      title="Bug-Reports"
      onBack="/admin"
      primaryAction={{
        label: 'Aktualisieren',
        onClick: loadBugReports
      }}
    >
      {error && (
        <AlertBanner
          variant="danger"
          message={error}
          onDismiss={() => setError('')}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Gesamt</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4 border border-red-200 dark:border-red-700">
          <div className="text-2xl font-bold text-red-900 dark:text-red-200">{stats.new}</div>
          <div className="text-sm text-red-600 dark:text-red-400">Neu</div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700">
          <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">{stats.inProgress}</div>
          <div className="text-sm text-yellow-600 dark:text-yellow-400">In Bearbeitung</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 border border-green-200 dark:border-green-700">
          <div className="text-2xl font-bold text-green-900 dark:text-green-200">{stats.resolved}</div>
          <div className="text-sm text-green-600 dark:text-green-400">Gelöst</div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.closed}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Geschlossen</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">Alle</option>
              <option value="new">Neu</option>
              <option value="in-progress">In Bearbeitung</option>
              <option value="resolved">Gelöst</option>
              <option value="closed">Geschlossen</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kategorie
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">Alle</option>
              <option value="ui">UI/Design</option>
              <option value="functionality">Funktionalität</option>
              <option value="performance">Performance</option>
              <option value="data">Daten</option>
              <option value="other">Sonstiges</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Suche
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Titel, Beschreibung, Email..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Bug Reports Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {filteredReports.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <div className="text-4xl mb-3">No Reports</div>
            <p>Keine Bug-Reports gefunden</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Titel
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kategorie
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Priorität
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Benutzer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredReports.map(report => (
                  <tr 
                    key={report.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => setSelectedReport(report)}
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {report.title}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">
                        {report.description}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(report.category)}`}>
                        {report.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={report.status}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateStatus(report.id, e.target.value);
                        }}
                        className={`text-xs font-medium rounded-full px-2 py-1 border-0 ${getStatusColor(report.status)}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="new">Neu</option>
                        <option value="in-progress">In Bearbeitung</option>
                        <option value="resolved">Gelöst</option>
                        <option value="closed">Geschlossen</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={report.priority}
                        onChange={(e) => {
                          e.stopPropagation();
                          updatePriority(report.id, e.target.value);
                        }}
                        className={`text-xs font-medium ${getPriorityColor(report.priority)}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="low">Niedrig</option>
                        <option value="medium">Mittel</option>
                        <option value="high">Hoch</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-900 dark:text-white">
                        {report.userEmail || 'Unbekannt'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(report.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteBugReport(report.id);
                        }}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedReport(null)}>
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {selectedReport.title}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(selectedReport.category)}`}>
                      {selectedReport.category}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedReport.status)}`}>
                      {selectedReport.status}
                    </span>
                    <span className={`text-xs font-medium ${getPriorityColor(selectedReport.priority)}`}>
                      Priorität: {selectedReport.priority}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Description */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Beschreibung</h3>
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{selectedReport.description}</p>
              </div>

              {/* Steps to Reproduce */}
              {selectedReport.stepsToReproduce && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Schritte zur Reproduktion</h3>
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{selectedReport.stepsToReproduce}</p>
                </div>
              )}

              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Benutzer</h3>
                  <p className="text-gray-900 dark:text-white">{selectedReport.userEmail || 'Unbekannt'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Datum</h3>
                  <p className="text-gray-900 dark:text-white">{formatDate(selectedReport.createdAt)}</p>
                </div>
              </div>

              {/* System Info */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">System-Informationen</h3>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Browser:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{selectedReport.browser} {selectedReport.browserVersion}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">OS:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{selectedReport.os} {selectedReport.osVersion}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Viewport:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {selectedReport.viewport?.width}x{selectedReport.viewport?.height}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Theme:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{selectedReport.theme}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">URL:</span>
                    <span className="ml-2 text-gray-900 dark:text-white break-all">{selectedReport.url}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between">
              <button
                onClick={() => deleteBugReport(selectedReport.id)}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Löschen
              </button>
              <button
                onClick={() => setSelectedReport(null)}
                className="px-4 py-2 text-sm font-medium bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </ViewLayout2>
  );
}
